<template>
    <v-card flat class="rounded-lg" width="70%">
        <v-card-title class="pa-6 text-h5">
            Login
        </v-card-title>
        <v-card-subtitle class="px-6 pt-0 pb-3 mt-n6 text-subtitle-1">
            Please login to your account
        </v-card-subtitle>
        <v-card-text class="pa-6 pt-0">
            <v-form v-model="valid" ref="form" @submit.prevent="handleEnter">
                <v-text-field
                    v-model="email"
                    :rules="[rules.required, rules.email]"
                    label="Email"
                    required
                />
                <v-text-field
                    v-if="verifyEmailAndPassword || verifyCodeAndPassword"
                    v-model="password"
                    :append-icon="show ? 'mdi-eye-off' : 'mdi-eye'"
                    :rules="[rules.required]"
                    :type="show ? 'text' : 'password'"
                    label="Password"
                    required
                    @click:append="show = !show"
                />
                <div v-if="verifyCodeAndPassword">
                    <span
                        >Enter the 6-digit verification code that was sent to
                        your email address.</span
                    >
                    <div class="d-flex flex-grow">
                        <v-text-field
                            v-model="code"
                            class="mr-2"
                            :rules="[rules.required, rules.code]"
                            label="Code"
                            required
                        />
                        <v-btn
                            text
                            color="primary"
                            class="my-4"
                            @click="verifyEmail(true)"
                            >resend code</v-btn
                        >
                    </div>
                </div>
                <v-card-actions class="pa-6">
                    <v-btn
                        v-if="verifyEmailAndPassword"
                        :loading="loading"
                        block
                        color="primary"
                        depressed
                        :disabled="!valid"
                        @click="signIn"
                        >Login</v-btn
                    >
                    <v-btn
                        v-if="verifyCodeAndPassword"
                        :loading="loading"
                        block
                        color="primary"
                        type="submit"
                        depressed
                        :disabled="!valid"
                        @click="verifyCode"
                        >Login</v-btn
                    >
                    <v-btn
                        v-if="!verifyEmailAndPassword && !verifyCodeAndPassword"
                        :loading="loading"
                        block
                        color="primary"
                        depressed
                        :disabled="!email"
                        @click="verifyEmail(false)"
                        >SEND</v-btn
                    >
                </v-card-actions>
                <div class="text-center">
                    <router-link :to="{ name: 'reset password' }">
                        Forgot password
                    </router-link>
                </div>
            </v-form>
        </v-card-text>
        <!--Error Alert-->
        <Errors />
        <!--Warning-->
        <v-alert v-if="warning" dismissible :type="warningType" class="mx-6">{{
            alertMessage
        }}</v-alert>
    </v-card>
</template>

<script>
import { auth, messaging, vapidKey } from '../services/firebase'
import API from '../services/api'
import { applyDocumentTitle } from '@/utils/companyNames'
import { mapActions, mapMutations } from 'vuex'

export default {
    name: 'SignIn',
    components: {
        Errors: () => import('@/components/Layout/Errors.vue'),
    },
    data() {
        return {
            warning: false,
            verifyEmailAndPassword: false,
            verifyCodeAndPassword: false,
            loginState: {},
            loading: false,
            valid: false,
            error: false,
            errorMsg: null,
            rules: {
                required: v => !!v || 'Required',
                email: v => /.+@.+\..+/.test(v) || 'Not a valid email',
                code: v =>
                    (/^[A-Za-z0-9]+$/g.test(v) && v.length == 6) ||
                    'Not a valid code',
            },
            email: '',
            password: '',
            code: '',
            show: false,
            currentToken: undefined,
            alertMessage: '',
            warningType: 'warning',
            failedLogins: 0,
        }
    },
    watch: {
        email: function() {
            this.email = this.email.trim()
        },
    },
    mounted() {
        Notification.requestPermission().then(permission => {
            if (permission === 'granted' && messaging) {
                messaging
                    .getToken({ vapidKey })
                    .then(currentToken => {
                        this.currentToken = currentToken
                    })
                    .catch(err => {
                        this.error = true
                        this.errorMsg = err.message
                    })
            }
        })
    },
    methods: {
        ...mapActions(['actionSaveUserRef']),
        ...mapMutations(['setErrorItems']),
        handleEnter() {
            if (this.loading) return

            if (this.verifyEmailAndPassword) {
                this.signIn()
            } else if (this.verifyCodeAndPassword) {
                this.verifyCode()
            } else if (this.email) {
                this.verifyEmail()
            }
        },
        async verifyCode() {
            try {
                this.loading = true
                const user = await API.verifyCode(this.email, this.code)
                if (user.authorized) {
                    await this.signIn()
                }
            } catch (error) {
                // if (this.failedLogins >= 2) {
                //     await API.disabledUserByEmail(this.email)
                //     this.failedLogins = 0
                // } else {
                //     this.failedLogins += 1
                // }
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        async verifyEmail(resend = false) {
            try {
                this.loading = true
                const user = await API.verifyEmail(this.email, resend)
                if (user.operator) {
                    this.verifyEmailAndPassword = true
                } else {
                    this.verifyCodeAndPassword = true
                }
            } catch (error) {
                if (error.message == 'User not verify') {
                    this.warningType = 'warning'
                    this.alertMessage =
                        'Welcome new user! Please check your email to obtain your initial password.'
                    this.warning = true
                } else if (error.name == 'resetPassword') {
                    await auth().sendPasswordResetEmail(this.email)
                    this.alertMessage = error.message
                    this.warningType = 'warning'
                    this.warning = true
                } else if (error.name == 'disabledUser') {
                    this.alertMessage = error.message
                    this.warningType = 'error'
                    this.warning = true
                } else {
                    this.setErrorItems({
                        source: this.$options.name,
                        message: error.message,
                    })
                }
            } finally {
                this.loading = false
            }
        },
        signIn: async function() {
            try {
                this.loading = true
                await auth().signInWithEmailAndPassword(
                    this.email,
                    this.password
                )
                //
                const {
                    data: { user },
                } = await API.getMyInfo()
                await API.logLogin()
                this.actionSaveUserRef(user)
                localStorage.setItem('userId', JSON.stringify(user.id))
                localStorage.setItem('user', JSON.stringify(user))
                localStorage.setItem('company', JSON.stringify(user.companyId))
                applyDocumentTitle()
                let path = user.permissions.includes('readTasks')
                    ? 'tasks'
                    : 'main-board'
                path = user.operatorRole ? 'operator' : path
                this.$router.push({ path }).catch(error => error && null)
            } catch (error) {
                // if (this.failedLogins >= 2) {
                //     await API.disabledUserByEmail(this.email)
                //     this.failedLogins = 0
                // } else {
                //     this.failedLogins += 1
                // }
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
                try {
                    // subscribe to topic for notifications
                    if (this.currentToken && messaging)
                        await API.subscribeToTopic(this.currentToken)
                } catch (error) {
                    console.log('notification error')
                }
            }
        },
    },
}
</script>
